import moment from 'moment-timezone'
import {
  fetchProductPresenceFromBackend,
  fetchProductsFromBackend
} from '@/services/products'

const TTL = 30 // Objects TTL in minutes

const PRODUCT_STATUSES = {
  LIVE: 'LIVE',
  LIVE_FUTURE: 'LIVE_FUTURE',
  DRAFT: 'DRAFT',
  ARCHIVE: 'ARCHIVE'
}

const FULL_FETCH_PAGE_SIZE = 250

/**
 * Mutation types of the store
 */
const mutationTypes = {
  FETCHING: 'FETCHING',
  FETCHED: 'FETCHED',
  HYDRATE_FROM_CACHE: 'HYDRATE_FROM_CACHE',
  FETCHING_PRESENCE: 'FETCHING_PRESENCE',
  FETCHED_PRESENCE: 'FETCHED_PRESENCE',
  INVALIDATE_SUPPLIER_PRODUCT_CACHE: 'INVALIDATE_SUPPLIER_PRODUCT_CACHE',
  UPSERT_LOCAL_PRODUCT: 'UPSERT_LOCAL_PRODUCT',
  REMOVE_LOCAL_PRODUCTS: 'REMOVE_LOCAL_PRODUCTS'
}

/**
 * Initial state
 */
const state = {
  loading: false,
  loadedAt: false,
  loadedKey: null,
  loadedRequest: null,
  products: [],
  productCacheByKey: {},
  hasAnyProducts: false,
  presenceLoading: false,
  presenceLoaded: false,
  presenceLoadedAt: false,
  presenceLoadedSupplierId: null,
  pagination: {
    total: 0,
    totalPages: 0
  }
}

const isFreshCacheEntry = (cacheEntry) =>
  !!(
    cacheEntry?.loadedAt &&
    cacheEntry.loadedAt.clone().add(TTL, 'minutes').isAfter(moment())
  )

const cacheActiveProductList = (state) => {
  if (!state.loadedKey || !state.productCacheByKey[state.loadedKey]) return

  state.productCacheByKey = {
    ...state.productCacheByKey,
    [state.loadedKey]: {
      ...state.productCacheByKey[state.loadedKey],
      products: state.products,
      pagination: state.pagination,
      loadedAt: moment()
    }
  }
}

/**
 * Actions
 */
const loadAllProductPages = (
  supplierId,
  status,
  size,
  query,
  brands,
  categories,
  pageNumber = 0,
  products = [],
  pagination = { total: 0, totalPages: 0 }
) =>
  fetchProductsFromBackend(
    supplierId,
    status,
    pageNumber,
    size || FULL_FETCH_PAGE_SIZE,
    query,
    brands,
    categories
  ).then((response) => {
    const pageProducts = Array.isArray(response)
      ? response
      : response.products || []
    const pagePagination = Array.isArray(response)
      ? {
          total: pageProducts.length,
          totalPages: pageProducts.length ? 1 : 0
        }
      : response.pagination || { total: 0, totalPages: 0 }
    const nextProducts = products.concat(pageProducts)

    if (
      Array.isArray(response) ||
      pageNumber + 1 >= pagePagination.totalPages
    ) {
      return {
        products: nextProducts,
        pagination: pagePagination
      }
    }

    return loadAllProductPages(
      supplierId,
      status,
      size,
      query,
      brands,
      categories,
      pageNumber + 1,
      nextProducts,
      pagePagination
    )
  })

const actions = {
  fetchProducts(
    { commit, state },
    {
      supplierId,
      force = false,
      status,
      page,
      size,
      query,
      brands,
      categories,
      all = false,
      background = false
    } = {}
  ) {
    const requestKeyParts = [supplierId, status || 'ALL']
    if (all) {
      requestKeyParts.push('FULL')
    }
    if (page !== undefined && size !== undefined) {
      requestKeyParts.push(page, size)
    }
    if (query) {
      requestKeyParts.push(query)
    }
    if (brands?.length) {
      requestKeyParts.push(`brands=${[...brands].sort().join(',')}`)
    }
    if (categories?.length) {
      requestKeyParts.push(`categories=${[...categories].sort().join(',')}`)
    }
    const requestKey = requestKeyParts.join(':')
    const requestOptions = {
      supplierId,
      status,
      page,
      size,
      query,
      brands,
      categories,
      all
    }
    const cachedEntry = state.productCacheByKey[requestKey]
    if (!force && isFreshCacheEntry(cachedEntry)) {
      if (state.loadedKey !== requestKey) {
        commit(mutationTypes.HYDRATE_FROM_CACHE, { requestKey })
      }
      return cachedEntry.products
    }

    if (!background) {
      commit(mutationTypes.FETCHING)
    }

    if (all) {
      return loadAllProductPages(
        supplierId,
        status,
        size,
        query,
        brands,
        categories
      ).then(({ products, pagination }) => {
        commit(mutationTypes.FETCHED, {
          products,
          pagination,
          requestKey,
          requestOptions
        })
        return { products, pagination }
      })
    }

    return fetchProductsFromBackend(
      supplierId,
      status,
      page,
      size,
      query,
      brands,
      categories
    ).then((response) => {
      const products = Array.isArray(response) ? response : response.products
      const pagination = Array.isArray(response)
        ? { total: products.length, totalPages: products.length ? 1 : 0 }
        : response.pagination
      commit(mutationTypes.FETCHED, {
        products,
        pagination,
        requestKey,
        requestOptions
      })
      return { products, pagination }
    })
  },

  fetchProductPresence({ commit, state }, { supplierId, force = false } = {}) {
    if (
      !force &&
      state.presenceLoadedSupplierId === supplierId &&
      state.presenceLoadedAt &&
      state.presenceLoadedAt.add(TTL, 'minutes').isAfter(moment())
    ) {
      return { hasProducts: state.hasAnyProducts }
    }

    commit(mutationTypes.FETCHING_PRESENCE)

    return fetchProductPresenceFromBackend(supplierId).then((response) => {
      const hasProducts = !!response?.hasProducts
      commit(mutationTypes.FETCHED_PRESENCE, { supplierId, hasProducts })
      return { hasProducts }
    })
  },

  refreshProductsForSupplierEvent(
    { dispatch, commit, state },
    { supplierId } = {}
  ) {
    commit(mutationTypes.INVALIDATE_SUPPLIER_PRODUCT_CACHE, { supplierId })

    if (!state.loadedRequest || state.loadedRequest.supplierId !== supplierId) {
      return Promise.resolve()
    }

    return dispatch('fetchProducts', {
      ...state.loadedRequest,
      force: true,
      background: true
    })
  },

  upsertProductLocally({ commit }, { product }) {
    commit(mutationTypes.UPSERT_LOCAL_PRODUCT, { product })
  },

  removeProductsLocally({ commit }, { productCodes, pageSize }) {
    commit(mutationTypes.REMOVE_LOCAL_PRODUCTS, { productCodes, pageSize })
  }
}

/**
 * Mutations
 */
const mutations = {
  [mutationTypes.FETCHING](state) {
    state.loading = true
  },

  [mutationTypes.FETCHED](state, payload) {
    const { products, pagination, requestKey, loadedAt = moment() } = payload
    state.loading = false
    state.products = products
    state.pagination = pagination
    state.loadedKey = requestKey
    state.loadedAt = loadedAt
    state.loadedRequest = payload.requestOptions || null
    state.productCacheByKey = {
      ...state.productCacheByKey,
      [requestKey]: {
        products,
        pagination,
        loadedAt,
        requestOptions: payload.requestOptions || null
      }
    }
  },

  [mutationTypes.HYDRATE_FROM_CACHE](state, payload) {
    const cachedEntry = state.productCacheByKey[payload.requestKey]
    if (!cachedEntry) return

    state.loading = false
    state.products = cachedEntry.products
    state.pagination = cachedEntry.pagination
    state.loadedKey = payload.requestKey
    state.loadedAt = cachedEntry.loadedAt
    state.loadedRequest = cachedEntry.requestOptions || null
  },

  [mutationTypes.FETCHING_PRESENCE](state) {
    state.presenceLoading = true
  },

  [mutationTypes.FETCHED_PRESENCE](state, payload) {
    state.presenceLoading = false
    state.presenceLoaded = true
    state.hasAnyProducts = payload.hasProducts
    state.presenceLoadedSupplierId = payload.supplierId
    state.presenceLoadedAt = moment()
  },

  [mutationTypes.INVALIDATE_SUPPLIER_PRODUCT_CACHE](state, payload = {}) {
    const supplierId = payload.supplierId
    if (!supplierId) {
      state.productCacheByKey = {}
      return
    }

    const supplierKeyPrefix = `${supplierId}:`
    state.productCacheByKey = Object.fromEntries(
      Object.entries(state.productCacheByKey).filter(
        ([key]) => !key.startsWith(supplierKeyPrefix)
      )
    )
  },

  [mutationTypes.UPSERT_LOCAL_PRODUCT](state, payload) {
    const index = state.products.findIndex(
      (product) => product.productCode === payload.product.productCode
    )
    if (index === -1) return
    state.products.splice(index, 1, payload.product)
  },

  [mutationTypes.REMOVE_LOCAL_PRODUCTS](state, payload) {
    const removedCodes = new Set(payload.productCodes)
    const nextProducts = state.products.filter(
      (product) => !removedCodes.has(product.productCode)
    )
    const removedCount = state.products.length - nextProducts.length

    state.products = nextProducts

    if (!removedCount) return

    const nextTotal = Math.max(0, (state.pagination.total || 0) - removedCount)
    const nextTotalPages = nextTotal
      ? Math.ceil(nextTotal / payload.pageSize)
      : 0

    state.pagination = {
      total: nextTotal,
      totalPages: nextTotalPages
    }
    cacheActiveProductList(state)
  }
}

/**
 * Getters
 */
const getters = {
  hasProducts(state) {
    return state.products.length > 0
  },

  hasAnyProducts(state) {
    return state.hasAnyProducts
  },

  liveProducts(state) {
    return state.products.filter((p) => p.status === PRODUCT_STATUSES.LIVE)
  },

  upcomingProducts(state) {
    return state.products.filter(
      (p) => p.status === PRODUCT_STATUSES.LIVE_FUTURE
    )
  },

  draftProducts(state) {
    return state.products.filter((p) => p.status === PRODUCT_STATUSES.DRAFT)
  },

  archiveProducts(state) {
    return state.products.filter((p) => p.status === PRODUCT_STATUSES.ARCHIVE)
  }
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state
}
